import React, { useMemo } from 'react';
import clsx from 'clsx';
import { Area, AreaChart, ResponsiveContainer, YAxis } from 'recharts';
import { TypedMemo } from '../../common/types';
import { useChartTokens } from '../theme/useChartTokens';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from './Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialSparklineProps {
  /** One number per day, oldest first. */
  values: number[];
  /** The numbers are old (the sign-in ended): the line is grey. */
  dim?: boolean;
  /** The 150 px size of the post detail. Default: the 40 px size of the channel card. */
  tall?: boolean;
  /** The id of the gradient. Two lines on one page need two ids. */
  gradientId: string;
}

/**
 * The growth line of a channel and the daily line of a post: a line over a soft area, with no
 * axis (design social-connectors `.spark`). It draws nothing for an empty list.
 */
const SocialSparklineComponent: React.FC<SocialSparklineProps> = ({
  values,
  dim = false,
  tall = false,
  gradientId,
}) => {
  const tokens = useChartTokens();
  const data = useMemo(() => values.map((value) => ({ value })), [values]);
  const stroke = dim ? tokens.label : tokens.brandLift;

  if (values.length === 0) {
    return <span className={clsx(styles.spark, tall && styles.sparkTall)} />;
  }

  return (
    <span className={clsx(styles.spark, tall && styles.sparkTall)} data-testid="social-sparkline">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 2, bottom: 0, left: 2 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={dim ? 0.09 : 0.26} />
              <stop offset="100%" stopColor={stroke} stopOpacity={0} />
            </linearGradient>
          </defs>
          <YAxis hide domain={['dataMin', 'dataMax']} />
          <Area
            type="linear"
            dataKey="value"
            stroke={stroke}
            strokeWidth={tall ? 2 : 1.5}
            fill={`url(#${gradientId})`}
            // One point has no line: the dot shows it.
            dot={values.length === 1 ? { r: 2.5, fill: stroke, strokeWidth: 0 } : false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </span>
  );
};

export const SocialSparkline = TypedMemo(SocialSparklineComponent);
